<template>
    <OptionsPanel ref="optionsPanel" cssClass="options-full-width">
        <template #options-results v-if="found || found === 0">
            {{
                found === 1 ?
                    $t('views.accounts.results_count.s', { found: found }) :
                    $t('views.accounts.results_count.p', { found: found })
            }}
        </template>
        
        <template #options-default>
            <div class="form-type-1">                
                <CustomInput
                    v-model="options.search"
                    inputKey="accounts_search"
                    @update:modelValue="onChangedDebounced"
                />
                <CustomSelect
                    v-model="options.sort"
                    inputKey="accounts_sort"
                    @update:modelValue="onChanged"
                    :options="sortOptions"
                />
            </div>
        </template>

        <template #options-full v-if="isPartnerGod">
            <div class="form-type-1">
                <CustomSelect
                    v-if="filterProductOptions?.length > 0"
                    v-model="options.product"
                    inputKey="options_product"
                    @update:modelValue="onChanged"
                    :options="filterProductOptions"
                />
                
                <CustomSelect
                    v-model="options.status"
                    inputKey="accounts_filter_status"
                    @update:modelValue="onChanged"
                    :options="filterStatusOptions"
                />
            </div>
        </template>

        <template #actions>
            <div :class="hasDefaultOptions ? 'btn-disabled' : 'btn-6'" @click="onReset()">
                <div class="icon-reset-neg"></div>
                <span>{{ $t('common.reset_1') }}</span>
            </div>
            <div class="btn-2" @click="createAccountPopup" v-if="isProspectPro">
                <div class="icon-plus-neg"></div>
                <span>{{ $t('forms.create_account.button') }}</span>
            </div>
        </template>
    </OptionsPanel>
</template>

<script>
    import debounce from 'lodash/debounce';
    import { mapState, mapActions, mapGetters } from 'pinia';
    import { useUserStore } from '@/stores/user';
    import { useAccountsStore } from '@/stores/accounts';
    import { useScreenWidthStore } from '@/stores/utils/screenWidth';
    import OptionsPanel from '@/components/panels/options/OptionsPanel';
    import CustomInput from '@/components/inputs/CustomInput';
    import CustomSelect from '@/components/inputs/CustomSelect';
    import i18n from "@/plugins/i18n";
    import { usePopupStore } from '@/stores/utils/popup';
    import { useAccountStore } from '@/stores/account';
    import { useToastStore } from '@/stores/utils/toast';
    import AccountCreateForm from '@/components/forms/AccountCreateForm';
    import { markRaw } from 'vue';

    export default {
        name: "AccountsOptions",
        components: {
            OptionsPanel,
            CustomInput,
            CustomSelect
        },
        data() {
            return {
                sortOptions: [
                    { value: 'name-asc', text: 'fields.accounts_sort.option_name_asc' },
                    { value: 'name-desc', text: 'fields.accounts_sort.option_name_desc' },
                    { value: 'product-asc', text: 'fields.accounts_sort.option_product_asc' },
                    { value: 'product-desc', text: 'fields.accounts_sort.option_product_desc' },
                ],
                filterStatusOptions: [
                    { value: '', text: 'fields.accounts_filter_status.option_all' },
                    { value: '1', text: 'fields.accounts_filter_status.option_1' },
                    { value: '-1', text: 'fields.accounts_filter_status.option_-1' },
                ],
                filterProductOptionsPp: [
                    { value: 'pp_free_v1', text: 'fields.options_product.options.pp_free_v1' },
                    { value: 'pp_trial_v1', text: 'fields.options_product.options.pp_trial_v1' },
                    { value: 'pp_frl_v1', text: 'fields.options_product.options.pp_frl_v1' },
                    { value: 'pp_unl_v1', text: 'fields.options_product.options.pp_unl_v1' },
                    { value: 'ma_free_v1', text: 'fields.options_product.options.ma_free_v1' },
                    { value: 'ma_trial_v1', text: 'fields.options_product.options.ma_trial_v1' },
                    { value: 'ma_frl_v1', text: 'fields.options_product.options.ma_frl_v1' },
                    { value: 'ma_unl_v1', text: 'fields.options_product.options.ma_unl_v1' },
                ],
                filterProductOptionsBa: [
                    { value: 'ba_freetrial_v1', text: 'fields.options_product.options.ba_freetrial_v1' },
                    { value: 'ba_basic_v1', text: 'fields.options_product.options.ba_basic_v1' },
                    { value: 'ba_freelancer_v1', text: 'fields.options_product.options.ba_freelancer_v1' },
                    { value: 'ba_small_v1', text: 'fields.options_product.options.ba_small_v1' },
                    { value: 'ba_large_v1', text: 'fields.options_product.options.ba_large_v1' },
                    { value: 'ma_freetrial_v1', text: 'fields.options_product.options.ma_freetrial_v1' },
                    { value: 'ma_basic_v1', text: 'fields.options_product.options.ma_basic_v1' },
                    { value: 'ma_freelancer_v1', text: 'fields.options_product.options.ma_freelancer_v1' },
                    { value: 'ma_small_v1', text: 'fields.options_product.options.ma_small_v1' },
                    { value: 'ma_large_v1', text: 'fields.options_product.options.ma_large_v1' }
                ],
            }
        },
        computed: {
            ...mapState(useAccountsStore, ['options', 'found']),
            ...mapGetters(useAccountsStore, ['hasDefaultOptions']),
            isProspectPro() {
                return process.env.VUE_APP_TYPE === 'bedrijfsdata';
            },
            filterProductOptions() {
                return process.env.VUE_APP_PRODUCT_PREFIX === 'pp' ? this.filterProductOptionsPp : process.env.VUE_APP_PRODUCT_PREFIX === 'ba' ? this.filterProductOptionsBa : [];
            },
            popupStore() {
                return usePopupStore();
            },
            toastStore() {
                return useToastStore();
            },
            accountStore() {
                return useAccountStore();
            },
            screenWidthStore() {
                return useScreenWidthStore();
            },
            hasLargeScreen() {
                return this.screenWidthStore.width >= 992;
            },
            isPartnerGod() {
                return useUserStore().isPartnerGod;
            }
        },
        methods: {
            ...mapActions(useAccountsStore, ['updateOptions', 'setUpdatingOptions', 'resetOptions']),
            onChanged() {
                this.updateOptions(this.options);
            },
            onChangedDebounced(value) {
                this.setUpdatingOptions(true);
                this.options.search = value;
                this.updateDebounced();
            },
            updateDebounced: debounce(function() {
                this.updateOptions(this.options);
            }, 500),
            onReset() {
                this.resetOptions();
                this.$refs.optionsPanel.toggleOptions();
            },
            createAccountPopup() {
                this.popupStore.showPopup('form', {
                    title: 'popups.create_account.title',
                    description: 'popups.create_account.description',
                    formComponent: markRaw(AccountCreateForm),
                    onFormSuccess: () => {
                        this.toastStore.addToast(i18n.global.t('toasts.success.account_created'), 'success');
                    }
                });
            },
        }
    }
</script>